"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AmplifyAuthAdapter = void 0;
const aws_amplify_1 = require("aws-amplify");
const auth_1 = require("aws-amplify/auth");
const cognito_1 = require("aws-amplify/auth/cognito");
const utils_1 = require("aws-amplify/utils");
// Importing aws-amplify/auth registers Amplify's auto OAuth listener,
// which is what detects `?code=...` on page reload after Cognito Hosted UI
// redirects back to redirectSignIn and exchanges it for tokens.
require("aws-amplify/auth");
const AccessToken_1 = require("./AccessToken");
/* eslint-disable no-useless-catch */
class AmplifyAuthAdapter {
    constructor(amplify) {
        var _a, _b, _c, _d;
        // Cross-tab plumbing — lazy-initialized shared resources.
        // Every call to subscribeToCrossTabAuthChanges used to create its own
        // BroadcastChannel + Hub.listen pair. With N subscribers (or one
        // subscriber that re-subscribes on isLoggedIn changes — see
        // apps/web-app MultiTabAuthSync), that's N OS-level channels + N
        // Amplify Hub listeners for identical work. Now: a single channel +
        // single Hub subscription for the whole adapter instance, fanning out
        // to the subscriber Set on message receipt.
        this.crossTabChannel = null;
        this.crossTabHubUnsubscribe = null;
        this.crossTabSubscribers = new Set();
        aws_amplify_1.Amplify.configure(amplify);
        if (amplify.cookieStorage) {
            const cs = amplify.cookieStorage;
            cognito_1.cognitoUserPoolsTokenProvider.setKeyValueStorage(new utils_1.CookieStorage(Object.assign(Object.assign({}, (cs.domain !== undefined && { domain: cs.domain })), { secure: (_a = cs.secure) !== null && _a !== void 0 ? _a : true, path: (_b = cs.path) !== null && _b !== void 0 ? _b : "/", expires: (_c = cs.expires) !== null && _c !== void 0 ? _c : 30, sameSite: (_d = cs.sameSite) !== null && _d !== void 0 ? _d : "lax" })));
        }
        else if (amplify.keyValueStorage) {
            // Native path: SecureStore-backed key-value storage. Same interface as
            // Amplify's CookieStorage / MemoryStorage — get/set/remove/clear.
            cognito_1.cognitoUserPoolsTokenProvider.setKeyValueStorage(amplify.keyValueStorage);
        }
    }
    subscribeToCrossTabAuthChanges(callback) {
        this.crossTabSubscribers.add(callback);
        // Lazy-init the shared channel + Hub listener on FIRST subscriber.
        // All subsequent subscribers reuse the same channel + listener.
        if (this.crossTabSubscribers.size === 1) {
            this.crossTabChannel =
                typeof BroadcastChannel !== "undefined"
                    ? new BroadcastChannel(AmplifyAuthAdapter.AUTH_CHANNEL_NAME)
                    : null;
            this.crossTabHubUnsubscribe = utils_1.Hub.listen("auth", ({ payload }) => {
                var _a, _b;
                if (payload.event === "signedIn") {
                    (_a = this.crossTabChannel) === null || _a === void 0 ? void 0 : _a.postMessage(true);
                }
                else if (payload.event === "signedOut") {
                    (_b = this.crossTabChannel) === null || _b === void 0 ? void 0 : _b.postMessage(false);
                }
            });
            if (this.crossTabChannel) {
                this.crossTabChannel.onmessage = event => {
                    const isAuthenticated = event.data === true;
                    // Snapshot to avoid mutation-during-iteration if a subscriber
                    // unsubscribes itself in response to the message.
                    [...this.crossTabSubscribers].forEach(cb => cb(isAuthenticated));
                };
            }
        }
        return () => {
            var _a, _b;
            this.crossTabSubscribers.delete(callback);
            // Tear down the shared plumbing when the last subscriber leaves.
            if (this.crossTabSubscribers.size === 0) {
                (_a = this.crossTabHubUnsubscribe) === null || _a === void 0 ? void 0 : _a.call(this);
                this.crossTabHubUnsubscribe = null;
                (_b = this.crossTabChannel) === null || _b === void 0 ? void 0 : _b.close();
                this.crossTabChannel = null;
            }
        };
    }
    login(loginData) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                const response = yield (0, auth_1.signIn)({
                    username: loginData.email,
                    password: loginData.password,
                });
                return {
                    passwordChangeRequired: ((_a = response === null || response === void 0 ? void 0 : response.nextStep) === null || _a === void 0 ? void 0 : _a.signInStep) ===
                        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED",
                    isLoggedIn: response.isSignedIn,
                };
            }
            catch (e) {
                return {
                    passwordChangeRequired: false,
                    isLoggedIn: false,
                };
            }
        });
    }
    forgotPassword(email) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield (0, auth_1.resetPassword)({ username: email });
            }
            catch (e) {
                throw new Error("Oops! Something went wrong, try again later!");
            }
        });
    }
    resetPassword(email, otp, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield (0, auth_1.confirmResetPassword)({
                    username: email,
                    confirmationCode: otp,
                    newPassword,
                });
            }
            catch (e) {
                throw e;
            }
        });
    }
    setPassword(newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield (0, auth_1.confirmSignIn)({
                    challengeResponse: newPassword,
                });
                yield (0, auth_1.signOut)();
            }
            catch (e) {
                throw e;
            }
        });
    }
    changePassword(oldPassword, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield (0, auth_1.updatePassword)({
                    oldPassword,
                    newPassword,
                });
            }
            catch (e) {
                throw e;
            }
        });
    }
    getAccessToken() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                const session = yield (0, auth_1.fetchAuthSession)();
                const idToken = (_b = (_a = session.tokens) === null || _a === void 0 ? void 0 : _a.idToken) === null || _b === void 0 ? void 0 : _b.toString();
                return new AccessToken_1.AccessToken(idToken || null);
            }
            catch (e) {
                return new AccessToken_1.AccessToken(null);
            }
        });
    }
    isAuthenticated() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield (0, auth_1.getCurrentUser)();
                const session = yield (0, auth_1.fetchAuthSession)();
                return !!session.tokens;
            }
            catch (e) {
                return false;
            }
        });
    }
    // Both local seams stay browser-free: force-logout reaches them unprompted
    // (401/403, socket reconnect on a transiently-null token, and every
    // UnauthenticatedDomain.init()), where the in-app browser can't present and
    // Amplify throws OAuthSignOutException.
    logOut() {
        return __awaiter(this, void 0, void 0, function* () {
            yield cognito_1.cognitoUserPoolsTokenProvider.tokenOrchestrator.clearTokens();
        });
    }
    clearAuthSession() {
        return __awaiter(this, void 0, void 0, function* () {
            yield cognito_1.cognitoUserPoolsTokenProvider.tokenOrchestrator.clearTokens();
        });
    }
    /** Visits Cognito /oauth2/logout to clear the SSO cookie so the next
     *  sign-in re-prompts. User-initiated logout only. */
    signOutFederated() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield (0, auth_1.signOut)();
            }
            catch (_a) {
                // Amplify skips completeOAuthSignOut unless the logout endpoint was
                // reached, leaving tokens behind — an unreachable cookie isn't worth
                // stranding the user in a session they asked to leave.
                yield cognito_1.cognitoUserPoolsTokenProvider.tokenOrchestrator.clearTokens();
            }
        });
    }
    signInWithRedirect() {
        return __awaiter(this, void 0, void 0, function* () {
            yield (0, auth_1.signInWithRedirect)();
        });
    }
    subscribeToSignInRedirectComplete(callback) {
        const unsubscribe = utils_1.Hub.listen("auth", ({ payload }) => {
            if (payload.event === "signInWithRedirect") {
                callback({ success: true });
            }
            else if (payload.event === "signInWithRedirect_failure") {
                // Frontend-owned error string. We deliberately do NOT propagate
                // Amplify's payload.data.error.message — that's SDK-internal
                // text ("User pool client does not exist", "redirect_mismatch",
                // etc.) which leaks raw OAuth / Cognito vocabulary to end users
                // and isn't reviewable or localizable. Keep a generic FE-owned
                // string; if a future need calls for distinct failure-mode UI,
                // switch SignInRedirectResult.error to a kind-based discriminator
                // and let the host app map kinds to its own i18n strings.
                callback({
                    success: false,
                    error: "Sign-in failed. Please try again.",
                });
            }
        });
        return unsubscribe;
    }
}
exports.AmplifyAuthAdapter = AmplifyAuthAdapter;
AmplifyAuthAdapter.AUTH_CHANNEL_NAME = "hhn-auth-state";
